/** Profile Tracker strings (extracted to keep en.ts under the size ratchet). */
import { enDeepResearchPanel } from "./enDeepResearchPanel";
import { enSignalTimeline } from "./enSignalTimeline";

export const enProfileTracker = {
	// PREC-4286: the price comes from JOB_CHANGE_CHECK_CREDITS, the number the API charges.
	trackJobChange:
		"Track job changes of added personal profiles (costs {{credits}} credits per profile checked per month)",
	search: "Search for profile tracker",
	emptyState: {
		title: "No companies tracked yet",
		description:
			"Add a company, or import a file of companies, to start tracking them and surfacing signals.",
		addSource: "Import file / Add source",
		addCompany: "Add company",
	},
	nameVariations: "Use name variations of companies to allow for more signals",
	status: {
		title: "Status",
	},
	name: "Name",
	namePlaceholder: "Name of the profile tracker",
	lastModifiedAt: "Last modified at",
	editProfileTracker: "Edit Custom Signal Tracker",
	XLSX: "Excel",
	EMPTY: "Empty",
	LISTS: "Lists",
	create: {
		button: "Create new profile tracker",
		title: "Create new profile tracker",
	},
	title: "Custom Signal Tracker",
	activity: {
		all: "All",
		running: "Running",
		stopped: "Stopped",
	},
	sourceType: "Source",
	empty: "Empty",
	xlsx: "Excel",
	list: "Lists",
	deleteProfileTracker: "Delete Custom Signal Tracker",
	signalsIn: "Signals",
	LAST_24H: "Last 24h",
	LAST_7D: "Last 7 days",
	LAST_30D: "Last 30 days",
	LAST_90D: "Last 90 days",
	LAST_180D: "Last 180 days",
	LAST_365D: "Last 365 days",
	signals: "Signals",
	tabs: {
		OVERVIEW: "Overview",
		RESEARCH: "Research",
		TIMELINE: "Timeline",
		PROFILES: "Profiles",
	},
	delete: {
		title: "Delete Custom Signal Tracker",
		confirmation: "Are you sure you want to delete this profile tracker?",
	},
	deepResearch: enDeepResearchPanel,
	signalColumn: {
		lookingForSignals: "Looking for signals",
		notScanned: "Not scanned yet",
		noRelevantSignals: "No relevant signals found",
		checkedOn: "checked {{date}}",
		runSearch: "Run signal search",
		runAgain: "Run again",
	},
	runSignalSearch: {
		title: "Run signal search",
		bulkAction: "Run signal search",
	},
	signalActions: {
		ignore: "Ignore signal",
		reachOutTo: "Write manually {{name}}",
		reachOutSignalInfoTitle: "Signal you're responding to",
		reachOutSignalInfoClose: "Dismiss signal",
		generateDrafts: "Generate 3 draft messages",
		generateNoSender:
			"You have no connected LinkedIn account, or mailbox for a lead with an email address, to generate drafts as.",
		generateAlreadyInProgress: "A draft generation is already in progress for this lead.",
		generateError: "Could not start draft generation. Please try again.",
		ignoreFeedbackTitle: "Why should this signal be ignored?",
		ignoreFeedbackDescription:
			"Share a short note so the signal model can learn from this feedback.",
		ignoreFeedbackLabel: "Feedback",
		ignoreFeedbackPlaceholder: "Tell us what was irrelevant or incorrect...",
		ignoreFeedbackRequired: "Please add a short feedback note.",
		ignoreFeedbackError: "Feedback could not be saved. Please try again.",
		ignoreSubmit: "Ignore signal",
	},
	signalTimeline: enSignalTimeline,
	/** The reworked Sources step of a Profile Tracker list (PREC-3417). */
	sources: {
		addSource: "Add a source",
		banner: {
			title: "Define the profiles you want to track",
			body:
				"The Profile Tracker watches specific people or companies for noteworthy activity. " +
				"Because we have to monitor a precise entity, every profile needs a clean identifier — " +
				"a personal LinkedIn URL for people, or a website for companies. Rows without one " +
				"cannot be tracked and are skipped; you can fix them and upload them again at any time.",
		},
		mode: {
			people: "Add people",
			companies: "Add companies",
			import: "Import from a list",
		},
		required: {
			people: "Each row needs a personal LinkedIn URL",
			companies: "Each row needs a company website or LinkedIn URL",
		},
		escape: {
			people: "No list of personal LinkedIn URLs yet?",
			companies: "No list of company websites yet?",
			createPeople: "Create people list first",
			createCompanies: "Create company list first",
		},
		name: {
			label: "Source name",
			peoplePlaceholder: "Name of the people file",
			companiesPlaceholder: "Name of the company file",
		},
		dropzone: {
			prompt: "Drag and drop a file here or",
			choose: "Choose file",
			peopleHint: ".xlsx, .xls or .csv · must include a personal LinkedIn URL column",
			companiesHint: ".xlsx, .xls or .csv · must include a company website or LinkedIn URL column",
		},
		mapping: {
			title: "Map your columns",
			subtitlePeople:
				"We found {{count}} columns in {{file}}. The required field — the personal LinkedIn " +
				"URL — is highlighted. Map it to continue; everything else can be saved as a custom " +
				"column or left out.",
			subtitleCompanies:
				"We found {{count}} columns in {{file}}. The required field — the company website or " +
				"LinkedIn URL — is highlighted. Map it to continue; everything else can be saved as a " +
				"custom column or left out.",
			columnHeader: "Column in your file",
			targetHeader: "Map to Signalist field",
			previewHeader: "Preview",
			requiredBadge: "Required",
			customFlag: "Will be saved as a custom column",
			ready: "Required field mapped",
			missing: "Map the required field to continue",
			sample: "e.g. {{value}}",
			analyzing: "Analysing your columns…",
			skip: "Do not import",
			custom: "＋ Save as custom column",
			submit: "Add source",
		},
		added: {
			title: "Added sources",
			emptyTitle: "No sources added",
			emptyDescription:
				"Add a people or company source on the left, or import leads from an existing list.",
			rows: "Rows in file",
			ready: "Ready to track",
			skipped: "Skipped",
			matched: "Matched leads",
			imported: "Imported",
			// PREC-4483: a row whose data could not be saved is skipped and counted here too.
			skippedPeople:
				"{{count}} rows skipped — no personal LinkedIn URL, or a value that could not be saved",
			skippedCompanies:
				"{{count}} rows skipped — no company website or LinkedIn URL, or a value that could not be saved",
			downloadSkipped: "Download skipped rows",
			trackingActive: "Tracking active",
			liveSyncOn: "Live sync on",
			tracking: "Tracking",
			liveImport: "Live import",
			paused: "Paused",
			people: "People",
			companies: "Companies",
			importedList: "Imported list",
			remove: "Remove source",
			removeConfirm:
				"Remove this source? Profiles it already added stay tracked, so their signal history " +
				"is kept.",
		},
		import: {
			listLabel: "Choose a list to import from",
			listPlaceholder: "Select a list",
			trackAsLabel: "What should be tracked?",
			trackPeople: "The leads themselves",
			trackCompanies: "The companies they work at",
			whichLabel: "Which leads should be imported?",
			presets: {
				ALL: "All leads",
				ALL_DESCRIPTION: "Every lead in the source list",
				QUALIFIED: "All qualified",
				QUALIFIED_DESCRIPTION: "P1, P2 and P3 — excludes disqualified",
				P1: "P1 only",
				P1_DESCRIPTION: "Highest-priority leads",
				P1_P2: "P1 + P2",
				P1_P2_DESCRIPTION: "High and moderate priority",
			},
			liveTitle: "Keep this import live",
			liveDescription:
				"When new matching leads are added to the source list, add them to this tracker too.",
			submit: "Import to tracker",
		},
		errors: {
			mappingRequired: "Map the required identifier before adding this source.",
			uploadFailed: "The source could not be added. Please try again.",
			importFailed: "The list could not be imported. Please try again.",
		},
		delete: {
			title: "Delete source",
		},
	},
	/** The account-wide switch for tracking positive repliers (PREC-3417). */
	positiveReplies: {
		title: "Positive replies",
		description:
			'A lead who answers "yes, interesting" is the strongest tracking candidate you get. ' +
			"Turn this on to add every positively replying lead to a Profile Tracker automatically.",
		toggleTitle: "Track leads who reply positively",
		toggleDescription:
			"Applies to replies on every channel, as soon as the reply is classified as interested.",
		destination: "Which tracker should they go to?",
		destinationPlaceholder: "Select a profile tracker",
		missingDestination: "Pick a tracker — until you do, nothing will be added.",
		overrideHint:
			"A lead list can send its own positive repliers to a different tracker in its settings. " +
			"Leaving that unset means it uses the tracker above.",
	},
	sort: "Sort",
	sorting: {
		nameASC: "Name (A–Z)",
		nameDESC: "Name (Z–A)",
	},
};
